import { mergeProductOverrides, Product, ProductWithVariants, ProductVariant } from '@models/product';
import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { InsuranceType } from '@models/insurance-type';
import { TOKEN_PRODUCT_CONFIG } from './token-product-config';
import { OrderQuery } from 'app/order/state/order.query';
import { toSignal } from '@angular/core/rxjs-interop';
import { compareProducts } from 'app/utils/helper.functions';
import { OrderState } from 'app/order/models';
import { distinctUntilChanged, filter, map } from 'rxjs';
import { Tag } from '@models/tag';

@Injectable({ providedIn: 'root' })
export class ProductsService {
    private orderQuery = inject(OrderQuery);

    public readonly product$ = this.orderQuery.state$.pipe(
        filter((orderState: OrderState) => !!orderState && !!orderState.productSlug),
        map((orderState) => this.getProductVariant(this.getBaseBySlug(orderState.productSlug), orderState)),
        distinctUntilChanged(compareProducts),
    );

    public readonly product = toSignal(this.product$);

    public readonly baseProduct: Signal<Product> = computed(
        () => (this.product() ? this.getBaseBySlug(this.product().slug) : null),
        {
            equal: (a, b) => a === b,
        },
    );

    private products = signal(inject<ProductWithVariants[]>(TOKEN_PRODUCT_CONFIG, { optional: true }) ?? []);
    private mergedProducts = computed(() => this.products().flatMap((product) => this.getProductAndVariants(product)));

    /**
     * @description returns the product for a given slug. searches both the base config
     * and variants of the product. The difference between this and {@link getBaseBySlug}
     * is that this will return the product with overrides applied when the slug is found
     * in a variant.
     * @param {string} slug slug of the product or product variant to be returned
     * @returns {Product | null} the product (with overrides applied if the slug
     * was found in a variant), or null if the slug does not belong to any product.
     */
    public getBySlug(slug: string): Product | null {
        const product = this.getBaseBySlug(slug);
        if (product) {
            if (product.slug === slug) {
                return product;
            }
            const variantForSlug = product.variants?.find((variant) => variant.overrides.slug === slug);
            if (variantForSlug) {
                return mergeProductOverrides(product, variantForSlug.overrides);
            }
            return product;
        }
        return null;
    }

    /**
     * @description returns the base product config for the given slug without
     * any overrides applied, unlike {@link getBySlug}, which applies overrides before returning
     * the product. Searches both the base config and all variants for the slug.
     * @param {string} slug product slug to search for.
     * @returns {ProductWithVariants | null} the base product for the slug without
     * any of the overrides applied, or null if the slug does not belong to any product.
     */
    public getBaseBySlug(slug: string): ProductWithVariants | null {
        for (const product of this.products()) {
            if (product.slug === slug || product.variants?.some((variant) => variant.overrides.slug === slug)) {
                return product;
            }
        }
        return null;
    }

    public getTagsForInsuranceTypeId(insuranceTypeId: number, tags: Tag[]): Partial<Record<Tag, boolean>> | null {
        const baseProduct = this.baseProduct();
        const product = this.getProductAndVariants(baseProduct).find(
            (item) => item.insuranceTypeId === insuranceTypeId,
        );

        if (!product) {
            return null;
        }

        return tags.reduce(
            (result, tag) => {
                result[tag] = product.tags.includes(tag);
                return result;
            },
            {} as Record<Tag, boolean>,
        );
    }

    public getByInsuranceType(insuranceType: InsuranceType): Product[] {
        if (!this.mergedProducts() || !insuranceType) {
            return [];
        }

        return this.mergedProducts().filter((product) => product.insuranceTypeId === insuranceType.id);
    }

    public getCanonicalByInsuranceType(insuranceType: InsuranceType): Product {
        const products: Product[] = this.getByInsuranceType(insuranceType);

        return products.find((product) => product.orderConfig?.canonical) ?? products[0];
    }

    public getInsuranceTypeIdForState(state: OrderState): number | null {
        const currentProduct = this.product();

        if (!currentProduct) {
            return null;
        }

        const baseProduct = this.getBaseBySlug(currentProduct.slug);
        const variant = this.getProductVariant(baseProduct, state);

        return variant?.insuranceTypeId ?? null;
    }

    private getProductVariant(baseProduct: ProductWithVariants, orderState: OrderState): Product {
        let productWithOverrides = { ...baseProduct };

        if (baseProduct.variants) {
            for (const variant of baseProduct.variants) {
                if (variant.condition(orderState)) {
                    productWithOverrides = mergeProductOverrides(baseProduct, variant.overrides);
                }
            }
        }
        return productWithOverrides;
    }

    private getProductAndVariants(product: ProductWithVariants): Product[] {
        const allVariants = [product];
        (product.variants ?? []).forEach((variant: ProductVariant) =>
            allVariants.push(mergeProductOverrides(product, variant.overrides)),
        );
        return allVariants;
    }
}
